import React, { useContext, useEffect, useState } from "react";
import * as yup from "yup";
import { InferType } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import { useUpdateTenantMutation } from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import { Formik, Form, Input } from "collections";
import { SubmitButton, CancelButton, DropdownList } from "elements";
import { useNotification, DISMISS } from "collections/Notifications";
import { useGuard, usePermissions, needs } from "rbac";
import { Tenant, TenantType } from "generated/graphql.schema";
import {
  placeholderTenant,
  SelectedTenantContext
} from "polyrouter/SelectedTenantProvider";

export const GeneralSettingsSchema = yup.object({
  accountName: yup
    .string()
    .required()
    .label(i18n.t("ACCOUNT_NAME")),
  tenantType: yup
    .string()
    .required()
    .label(i18n.t("TENANT_TYPE"))
});
type GeneralSettingsTypes = InferType<typeof GeneralSettingsSchema>;

const GeneralSettings: React.FC = () => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [{ error }, updateTenant] = useUpdateTenantMutation();
  const { tenant: selectedTenant, refetch } = useContext(SelectedTenantContext);
  const tenant = selectedTenant ?? placeholderTenant;

  const [tenantType, setTenantType] = useState(tenant.type);
  const hasPermission = usePermissions();

  const userCanEdit = useGuard(needs("UPDATE_TENANT"));
  const userCanModifyTenantType = hasPermission(
    "MODIFY_TENANT_TYPE",
    tenant.id
  );

  useEffect(() => {
    setTenantType(tenant.type);
  }, [tenant.type]);

  const submit = async (values: GeneralSettingsTypes) => {
    await updateTenant(
      {
        id: tenant.id,
        input: {
          name:
            values.accountName !== tenant.name
              ? (values.accountName as Tenant["name"])
              : undefined,
          type:
            values.tenantType !== tenant.type
              ? (values.tenantType as Tenant["type"])
              : undefined
        }
      },
      { i18nKey: "ERROR_UPDATING_ACCOUNT" }
    ).then(() => {
      refetch();
    });

    if (!error) {
      addNotification({
        id: "edit-success",
        type: "success",
        message: t("ACCOUNT_UPDATED"),
        dismiss: DISMISS.DEFAULT
      });
    }
  };

  const { Content } = Layout;
  return (
    <Content scrolling>
      <Content.ScrollingList>
        <div className="card title-card">
          {t("GENERAL")} {t("SETTINGS")}
        </div>
        <Formik
          name="GeneralSettings"
          initialValues={{
            accountName: tenant.name || "",
            tenantType: tenant.type || ""
          }}
          enableReinitialize
          validationSchema={GeneralSettingsSchema}
          onSubmit={submit}
        >
          {({
            dirty,
            handleReset,
            isSubmitting,
            field,
            isValid,
            setFieldValue
          }) => (
            <Form className="form grid" data-testid="GeneralSettingsForm">
              <div className="form-controls pb-5">
                <CancelButton
                  className="btn"
                  disabled={isSubmitting || !dirty}
                  onClick={handleReset}
                >
                  {t("DISCARD")}
                </CancelButton>
                <SubmitButton
                  isSubmitting={isSubmitting}
                  data-testid="GeneralSettingsFormSubmit"
                  disabled={!dirty || !isValid}
                />
              </div>
              <div className="form-group">
                <label
                  className="col-3 text-right"
                  htmlFor={"accountName-input"}
                >
                  {field.accountName.label}:
                </label>
                <div className="col-3">
                  <Input
                    name={field.accountName.name}
                    data-testid={field.accountName["data-testid"]}
                    disabled={!userCanEdit}
                  />
                </div>
                <label
                  className="col-3 text-right"
                  style={{ gridRowStart: 2 }}
                  htmlFor={field.tenantType.name + "-drop-down-input"}
                >
                  {field.tenantType.label}:
                </label>
                <div
                  className="col-3"
                  style={{
                    gridRowStart: 2,
                    paddingLeft: 10
                  }}
                >
                  <DropdownList
                    name={field.tenantType.name + "-drop-down-input"}
                    onChange={tenantType => {
                      setFieldValue(
                        field.tenantType.name,
                        tenantType as TenantType
                      );
                      setTenantType(tenantType as TenantType);
                    }}
                    value={tenantType}
                    disabled={!userCanModifyTenantType}
                    options={Object.values(TenantType).map(tenantType => ({
                      value: tenantType,
                      text: t(tenantType)
                    }))}
                  />
                </div>
                {userCanModifyTenantType && tenantType !== tenant.type && (
                  <div
                    className="col-3 offset-3"
                    style={{
                      gridRowStart: 3,
                      color: "lightslategray"
                    }}
                  >
                    {t("WARNING_TENANT_TYPE_CHANGE")}
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Content.ScrollingList>
    </Content>
  );
};

export default GeneralSettings;
